import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import clsx from 'clsx';
import { formatDistanceToNowStrict } from 'date-fns';
import { faClock } from '@soundxyz/font-awesome/pro-solid-svg-icons/index.mjs';
import { useTimer } from '@components-shared/src/countdown/hooks/useTimer';
import ProgressCircle from '@components/ProgressCircle';
import { DateTimeTooltip } from '@components/main-components/datetime-tooltip';
import { DataTestId } from '@utils/test';

export function TimerBadge({
  startTimestamp,
  expiryTimestamp,
}: {
  startTimestamp: number;
  expiryTimestamp: number;
}) {
  const progressPercentage = Math.floor(timePercentage(startTimestamp, expiryTimestamp));
  const { seconds, minutes, hours, days } = useTimer({
    expiryTimestamp: expiryTimestamp,
  });

  const moreThan24 = days > 0;
  const distance = moreThan24
    ? formatDistanceToNowStrict(expiryTimestamp, { roundingMethod: 'floor' })
    : null;

  return (
    <DateTimeTooltip dateTime={new Date(expiryTimestamp)} prefix="Ends on ">
      <div
        data-testid={DataTestId.TIMER_BADGE}
        className={clsx(
          'flex items-center justify-between rounded-full bg-base900 pr-2.5 font-base text-base-s text-white',
          distance
            ? 'min-w-fit'
            : hours > 0
            ? 'min-w-[92px]'
            : minutes > 0
            ? 'min-w-[72px]'
            : 'w-fit',
        )}
      >
        {/* Element is moved 1px to the left to prevent the circular progress bar from perfectly overlapping the dark element behind it. This avoids jagged edges. */}
        <div className="relative flex h-6 w-6 -translate-x-[1px] items-center justify-center">
          <div className="absolute left-0 top-0">
            <ProgressCircle
              percent={progressPercentage}
              usedColor="#EB5CBB"
              bgColor="#FFF"
              remainingColor="#0F0F0F"
              size={24}
            />
          </div>
          <div className="z-above1 text-base800">
            <FontAwesomeIcon icon={faClock} size="xs" />
          </div>
        </div>
        <span className={clsx('font-semibold tabular-nums', moreThan24 ? 'pl-1.5' : 'pl-1')}>
          {moreThan24 ? (
            distance
          ) : (
            <>
              {hours > 0 && `${String(hours).padStart(2, '0')}:`}
              {minutes >= 0 && `${String(minutes).padStart(2, '0')}:`}
              {days === 0 && minutes >= 0 && seconds >= 0 && `${String(seconds).padStart(2, '0')}`}
            </>
          )}
        </span>
      </div>
    </DateTimeTooltip>
  );
}

export function timePercentage(startTimestamp: number, endTimestamp: number) {
  const startTime = new Date(startTimestamp);
  const endTime = new Date(endTimestamp);

  const totalTimeSpanMilliseconds = endTime.getTime() - startTime.getTime();

  const currentTime = new Date();
  const elapsedTimeMilliseconds = currentTime.getTime() - startTime.getTime();

  // Calculate the percentage of time passed
  const percentageTimePassed = (elapsedTimeMilliseconds / totalTimeSpanMilliseconds) * 100;

  return percentageTimePassed;
}
